import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isNil } from "lodash";
import moment from "moment";
import { Row, Col, Timeline, Skeleton, Tooltip } from "antd";
import useSession from "Hooks/session.hook";
import "../order-details/order-details.scss";
import "./delivery-updates.scss";
import deliveryTypeJSON from "Data/delivery-types.json";
import {
  CompassTwoTone,
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { BasicModal, Button } from "@life-line-lab/components-react";
import EditAddDeliveryUpdateModal from "./edit-add-delivery-update-modal";
import ConfirmModal from "Components/order-management/shared/parcel-modals/confirm-modal";
import useCorporateType from "Hooks/corporate-type.hook";

interface DeliveryUpdatesProps {
  handleEditDeliveryUpdates?: (input: any) => Promise<void>;
  handleAddDeliveryUpdates?: (input: any) => Promise<void>;
  deliveryUpdatesInformation: any;
  handleListingSortingHub?: (filters: any) => Promise<any[]>;
  handleDeleteDeliveryUpdates?: (input: any) => Promise<void>;
  isLoading: boolean;
}

// renders the number of skeleton set in the form of ===== O =====
const numberOfSkeletonSet = 3;

const getDeliveryDetails = (
  deliveryType: string,
  status: string,
  data: any,
  isPublic = true,
) => {
  const { deliveryUpdates } = deliveryTypeJSON;
  const orderCreatedMessage = "Order Created.";
  let message = "";

  const downloadImages = () => {
    const downloadUrls = data?.orderData?.deliveredOrderImageFileData?.map(
      (file: any) => {
        return () => window.open(file.file_url);
      },
    );

    downloadUrls.forEach((download: any, index: number) => {
      setTimeout(() => {
        download();
      }, index * 100);
    });
  };

  const coordinateTooltip = data.coordinates && !isPublic && (
    <Tooltip
      title={
        <>
          <div>Coordinates:</div>
          <div>
            {data.coordinates?.lat}, {data.coordinates?.lon}
          </div>
        </>
      }
      placement="topLeft"
      color="#262626"
    >
      <CompassTwoTone className="coordinate-tooltip" />
    </Tooltip>
  );

  switch (status) {
    case "CREATED":
      return orderCreatedMessage;
    case "DROPPED_OFF":
    case "RETURNED":
      message = data?.orderData?.midMileInfoData?.sortingHub?.locationName
        ? data?.orderData?.midMileInfoData?.sortingHub?.locationName
        : "";
      return `${deliveryUpdates?.[deliveryType]?.[status]} ${message}`;
    case "UNLOADED":
      message = data?.orderData?.midMileInfoData?.unloadAt?.locationName
        ? data?.orderData?.midMileInfoData?.unloadAt?.locationName
        : "";
      return `${deliveryUpdates?.[deliveryType]?.[status]} ${message}`;
    case "DELIVERED":
      return (
        <div className="image-container">
          {deliveryUpdates?.[deliveryType]?.[status]}
          {Array.isArray(data?.orderData?.deliveredOrderImageFileData) &&
            data.orderData.deliveredOrderImageFileData.length > 0 && (
              <div
                className="delivered-images-list"
                style={{ display: "flex", flexDirection: "column" }}
              >
                {data.orderData.deliveredOrderImageFileData.map(
                  (file: any, idx: number) => (
                    <a
                      key={file._id}
                      href={file.file_url}
                      target="_blank"
                      download
                      style={{ marginRight: 8 }}
                    >
                      Download Image_{idx + 1}
                    </a>
                  ),
                )}
              </div>
            )}
          {coordinateTooltip}
        </div>
      );
    case "DELIVERY_REFUSED":
    case "DELIVERY_NOT_POSSIBLE":
      message = data?.orderData?.failedDeliveryRemarks
        ? `Remarks: ${data?.orderData?.failedDeliveryRemarks}`
        : "";

      return (
        <>
          <span>{`${deliveryUpdates?.[deliveryType]?.[status]} ${message}`}</span>
          {coordinateTooltip}
        </>
      );

    case "PICKED_UP":
    case "REJECTED":
    case "CANCELLED":
      return (
        <>
          <span>{deliveryUpdates?.[deliveryType]?.[status]}</span>
          {coordinateTooltip}
        </>
      );
    default:
      return deliveryUpdates?.[deliveryType]?.[status];
  }
};

const DeliveryUpdates: React.FC<DeliveryUpdatesProps> = ({
  handleEditDeliveryUpdates,
  handleAddDeliveryUpdates,
  deliveryUpdatesInformation,
  handleListingSortingHub,
  isLoading,
  handleDeleteDeliveryUpdates,
}) => {
  const { t } = useTranslation();
  const { roles } = useSession();
  const { isCustomer } = useCorporateType();

  const isPublic = useMemo(() => {
    const userRole = !isNil(roles) ? roles[0]["type"] : "PUBLIC";
    return location.pathname.includes("track-trace") || userRole === "PUBLIC";
  }, []);

  const initialModal = {
    open: false,
    type: "",
    record: {},
  };

  const [callModal, setCallModal] = useState<any>(<></>);
  const [modal, setModal] = useState<any>(initialModal);
  const [error, setError] = useState<any>({});

  const getTimelineLabel = (updateObj) => {
    return (
      <>
        {updateObj.status !== "OPEN" && !isPublic && !isCustomer && (
          <>
            <span
              className="pov-edit button-edit-delete"
              onClick={() => {
                setCallModal(
                  <EditAddDeliveryUpdateModal
                    initialInput={{
                      timeStamp: moment(updateObj?.timeStamp),
                      status: updateObj.status,
                    }}
                    title={t("title.changeMilestone")}
                    onSubmit={(input: any) => {
                      handleSubmitModal(
                        {
                          timeStamp: input.timeStamp,
                          _id: updateObj._id,
                        },
                        true,
                      );
                    }}
                    onClose={() => setModal(initialModal)}
                    getDeliveryDetails={getDeliveryDetails}
                    error={error}
                    setError={setError}
                    isLoading={isLoading}
                    selectedOrder={updateObj}
                    handleListingSortingHub={async () => []}
                  />,
                );
                setModal({ open: true });
              }}
            >
              <EditOutlined />
            </span>
            <span
              className="pov-edit button-edit-delete"
              onClick={() => {
                setCallModal(
                  <ConfirmModal
                    onSubmit={() => {
                      handleDeleteDeliveryUpdates &&
                        handleDeleteDeliveryUpdates({ _id: updateObj._id });
                      setModal(initialModal);
                    }}
                    onCancel={() => setModal(initialModal)}
                    contentWord={t("txt.areYouSureDeleteMilestone")}
                  />,
                );
                setModal({ open: true });
              }}
            >
              <DeleteOutlined style={{ color: "hsl(0, 100%, 30%)" }} />
            </span>
          </>
        )}
        {updateObj?.timeStamp
          ? moment(updateObj?.timeStamp).format("DD-MM-YYYY") +
            " | " +
            moment(updateObj?.timeStamp).format("hh:mm a")
          : "-"}
      </>
    );
  };

  const addTimeline = () => {
    const { showStatus, deliveryUpdates } = deliveryTypeJSON;

    // there are some statuses that BE will send, but BA does not want them to be shown
    // we directly assign first order to the delivery update
    const originalUpdates: any =
      deliveryUpdatesInformation?.deliveryUpdates || [];
    let filteredDeliveryUpdates: any =
      originalUpdates.length > 0 ? [originalUpdates[0]] : [];

    for (let i = 0; i < originalUpdates?.length; i++) {
      const order = originalUpdates[i];

      // if the status has a statement in the delivery-types.json, consider that
      if (showStatus?.[order.status] && order?.deliveryType) {
        // if it is IN_BOX or IN_TROLLEY, check the next sibling
        // if next sibling is IN_BOX or IN_TROLLEY, skip this
        if (
          i < originalUpdates.length &&
          (order.status === "IN_BOX" || order.status === "IN_TROLLEY")
        ) {
          const siblingUpdate = originalUpdates[i + 1];

          if (
            siblingUpdate?.status === "IN_BOX" ||
            siblingUpdate?.status === "IN_TROLLEY"
          ) {
            continue;
          }
        }

        filteredDeliveryUpdates.push(order);
      }
    }

    // remove the update if the message is null
    filteredDeliveryUpdates = filteredDeliveryUpdates.filter(
      (updateObj) =>
        deliveryUpdates?.[updateObj.deliveryType]?.[updateObj.status] !== null,
    );

    // console.log("filtered", filteredDeliveryUpdates);
    // if ui shows empty statements, check delivery-types.json and ensure the respective field [deliveryType].[status] is not null
    // uncomment the console.log above to see what deliveryType and status has been filtered
    return (
      <div className="tnt-timeline">
        {filteredDeliveryUpdates?.length > 0 ? (
          <>
            <Timeline className="mx-auto" mode="left">
              {filteredDeliveryUpdates?.map((updateObj, index: number) => {
                return (
                  <Timeline.Item
                    label={getTimelineLabel(updateObj)}
                    key={index}
                  >
                    {getDeliveryDetails(
                      updateObj.deliveryType,
                      index === 0 ? "CREATED" : updateObj.status,
                      updateObj,
                      isPublic,
                    )}
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </>
        ) : (
          <div className="tnt-content-center">
            <span className="tnt-content-error">
              {t("txt.invalidTrackingNo")}
            </span>
          </div>
        )}
      </div>
    );
  };

  const handleSubmitModal = (input: any, isEdit = false) => {
    if (isEdit) {
      handleEditDeliveryUpdates && handleEditDeliveryUpdates(input);
    } else {
      const deliveryUpdate = deliveryUpdatesInformation?.deliveryUpdates[0];
      const payload = {
        deliveryUpdates: {
          orderID: deliveryUpdate.orderID,
          trackingNumber: deliveryUpdate.trackingNumber,
          status: input.status,
          timeStamp: input.timeStamp,
        },
        files: input.files,
        unloadAt: input.unloadAt,
        failedDeliveryRemarks: input.failedDeliveryRemarks,
      };

      handleAddDeliveryUpdates && handleAddDeliveryUpdates(payload);
    }

    setModal(initialModal);
  };

  return (
    <>
      <Row
        justify="end"
        className={isPublic ? "track-n-trace--details" : "details-container"}
      >
        {isLoading ? (
          <div className="loading-container no-height">
            <div className="tnt-skeleton">
              {Array(numberOfSkeletonSet)
                .fill(true)
                .map((_, index) => (
                  <div key={index} className="tnt-skeleton-set">
                    <Skeleton.Button
                      active
                      size="small"
                      className="skeleton-bar"
                    />
                    <Skeleton.Avatar active size="small" shape="circle" />
                    <Skeleton.Button
                      active
                      size="small"
                      className="skeleton-bar"
                    />
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <>
            {!isPublic && !isCustomer && (
              <Col>
                <Button
                  type="primary"
                  label={t("action.addMilestone")}
                  onClick={() => {
                    setCallModal(
                      <EditAddDeliveryUpdateModal
                        handleListingSortingHub={handleListingSortingHub}
                        title={t("action.addMilestone")}
                        onSubmit={(input: any) => {
                          handleSubmitModal(input);
                        }}
                        onClose={() => setModal(initialModal)}
                        getDeliveryDetails={getDeliveryDetails}
                        error={error}
                        setError={setError}
                        isLoading={isLoading}
                        selectedOrder={
                          deliveryUpdatesInformation?.deliveryUpdates[0]
                        }
                      />,
                    );
                    setModal({ open: true });
                  }}
                  icon={<PlusCircleOutlined />}
                />
              </Col>
            )}
            <Col span={24}>{addTimeline()}</Col>
          </>
        )}
      </Row>
      <BasicModal
        className="edit-add-modal"
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          setModal(initialModal);
        }}
        bodyStyle={{ width: "70vw", maxWidth: "500px", margin: "0 auto" }}
      >
        {callModal}
      </BasicModal>
    </>
  );
};

export default DeliveryUpdates;
